import React, { Suspense, lazy } from "react";
import { useGlobalData } from "../config/GlobalContext";
import { Route, Routes, Navigate, useLocation } from "react-router";
import LoadingSpinner from "../components/Loading/LoadingSpinner";

const Home = lazy(() => import("../pages/Home"));
const Cart = lazy(() => import("./../pages/Cart"));
const MapPage = lazy(() => import("../pages/Map/Map"));
const CardDetails = lazy(() => import("../pages/Bikes/BikeDetails"));

const Signup = lazy(() => import("../pages/Auth/Signup"));
const Signin = lazy(() => import("../pages/Auth/Signin"));
const Recover = lazy(() => import("../pages/Auth/Recover"));
const ResetPassword = lazy(() => import("../pages/Auth/ResetPassword"));

const Users = lazy(() => import("../pages/Dashboard/users/Users"));
const Bikes = lazy(() => import("../pages/Dashboard/bikes/Bikes"));
const Financy = lazy(() => import("../pages/Dashboard/stats/Financy"));
const BikeForm = lazy(() => import("../pages/Dashboard/bikes/BikeForm"));
const Rentals = lazy(() => import("../pages/Dashboard/rentals/Rentals"));
const StationForm = lazy(
  () => import("../pages/Dashboard/stations/StationForm")
);
const Stations = lazy(() => import("../pages/Dashboard/stations/Stations"));
const AddRental = lazy(() => import("../pages/Dashboard/rentals/AddRental"));
const SellerDashboard = lazy(
  () => import("../pages/Dashboard/SellerDashboard")
);
const AdminDashboard = lazy(() => import("../pages/Dashboard/AdminDashboard"));
const UserProfile = lazy(() => import("../pages/Dashboard/users/UserProfile"));
const RentalDetails = lazy(() => import("../pages/Rentals/RentalDetails"));
const Profile = lazy(() => import("../pages/Users/Profile"));

const Forbidden = lazy(() => import("../pages/Errors/ForbiddenPage"));
const TimeoutPage = lazy(() => import("../pages/Errors/TimeoutPage"));
const OfflinePage = lazy(() => import("../pages/Errors/OfflinePage"));
const NotFoundPage = lazy(() => import("../pages/Errors/NotFoundPage"));
const MaintenancePage = lazy(() => import("../pages/Errors/MaintenancePage"));
const ServerErrorPage = lazy(() => import("../pages/Errors/ServerErrorPage"));

type ProtectedProps = {
  children: React.ReactNode;
  accessProtectionLevel?: "operational" | "administrative" | "authenticated";
};

type UserRole = "Administrador" | "Cliente" | "Caixa";

enum AccessLevel {
  PUBLIC = "public",
  AUTHENTICATED = "authenticated",
  OPERATIONAL = "operational",
  ADMINISTRATIVE = "administrative",
}

const hasAccess = (
  userRole: UserRole | undefined,
  requiredLevel?: string
): boolean => {
  if (!userRole) return false;

  switch (requiredLevel) {
    case AccessLevel.PUBLIC:
      return true;

    case AccessLevel.AUTHENTICATED:
      return true;

    case AccessLevel.OPERATIONAL:
      return userRole === "Caixa" || userRole === "Administrador";

    case AccessLevel.ADMINISTRATIVE:
      return userRole === "Administrador";

    default:
      return true;
  }
};

/**
 * Hook personalizado para gerenciar autenticação e autorização
 */
const useAuth = () => {
  const { user } = useGlobalData();

  return {
    user,
    isAuthenticated: !!user,
    userRole: user?.role as UserRole | undefined,
    hasAccess: (requiredLevel?: string) =>
      hasAccess(user?.role as UserRole, requiredLevel),
  };
};

/**
 * Higher-order component que protege rotas baseado em autenticação e autorização.
 *
 * @param children - Componentes filhos a serem renderizados se o acesso for permitido
 * @param accessProtectionLevel - Nível de proteção da rota:
 *   - undefined/authenticated: Apenas requer autenticação (qualquer usuário logado)
 *   - "operational": Caixa + Administrador (operações do dia a dia)
 *   - "administrative": Apenas Administrador (configurações e relatórios)
 *
 * Hierarquia de Permissões:
 * - Cliente: Acesso apenas a rotas públicas e perfil
 * - Caixa: Operações + operações do dia a dia (alugueres, usuários)
 * - Administrador: Todos os acessos (relatórios, configurações, bicicletas, estações)
 *
 * Fluxo de verificação:
 * 1. Verifica se o usuário está autenticado
 * 2. Verifica se o usuário tem permissão para o nível de acesso
 * 3. Renderiza o conteúdo ou redireciona apropriadamente
 */
const Protected = ({ children, accessProtectionLevel }: ProtectedProps) => {
  const { isAuthenticated, hasAccess } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search + location.hash;
    return <Navigate to="/signin" state={{ from: currentPath }} replace />;
  }

  if (!hasAccess(accessProtectionLevel)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

type AuthRedirectProps = {
  children: React.ReactNode;
};

const AuthRedirect = (props: AuthRedirectProps) => {
  const { user } = useGlobalData();
  const location = useLocation();

  if (user) {
    const from = (location.state as any)?.from;
    const redirectTo = from || "/";

    return <Navigate to={redirectTo} replace />;
  }

  return props.children;
};

export default function AppRouter() {
  return (
    <Suspense
      fallback={<LoadingSpinner size="lg" message="Carregando página..." />}
    >
      <Routes>
        <Route
          path="/signin"
          element={
            <AuthRedirect>
              <Signin />
            </AuthRedirect>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRedirect>
              <Signup />
            </AuthRedirect>
          }
        />
        <Route
          path="/recover"
          element={
            <AuthRedirect>
              <Recover />
            </AuthRedirect>
          }
        />
        <Route
          path="/reset-password"
          element={
            <AuthRedirect>
              <ResetPassword />
            </AuthRedirect>
          }
        />

        {/* Public Routes */}

        <Route path="/" element={<Home />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/details/:id" element={<CardDetails />} />

        <Route path="/forbidden" element={<Forbidden />} />

        {/* Error Pages */}
        <Route path="/server-error" element={<ServerErrorPage />} />
        <Route path="/not-found" element={<NotFoundPage />} />
        <Route path="/offline" element={<OfflinePage />} />
        <Route path="/timeout" element={<TimeoutPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />

        <Route path="*" element={<NotFoundPage />} />

        {/* Protected Routes */}
        <Route
          path="/manage/addrental"
          element={
            <Protected accessProtectionLevel="operational">
              <AddRental />
            </Protected>
          }
        />
        <Route
          path="/manage/users"
          element={
            <Protected accessProtectionLevel="operational">
              <Users />
            </Protected>
          }
        />
        <Route
          path="/manage/rentals"
          element={
            <Protected accessProtectionLevel="operational">
              <Rentals />
            </Protected>
          }
        />
        <Route
          path="/manage/rentals/details/:id"
          element={
            <Protected accessProtectionLevel="operational">
              <RentalDetails />
            </Protected>
          }
        />
        <Route
          path="/profile"
          element={
            <Protected accessProtectionLevel="operational">
              <UserProfile />
            </Protected>
          }
        />

        {/* Rotas Administrativas - Apenas Administrador */}
        <Route
          path="/manage/financy"
          element={
            <Protected accessProtectionLevel="administrative">
              <Financy />
            </Protected>
          }
        />
        <Route
          path="/manage/bikes/add"
          element={
            <Protected accessProtectionLevel="administrative">
              <BikeForm />
            </Protected>
          }
        />
        <Route
          path="/manage/bikes/edit/:id"
          element={
            <Protected accessProtectionLevel="administrative">
              <BikeForm />
            </Protected>
          }
        />
        <Route
          path="/manage/bikes/list"
          element={
            <Protected accessProtectionLevel="administrative">
              <Bikes />
            </Protected>
          }
        />
        <Route
          path="/manage/bikes/details/:id"
          element={
            <Protected accessProtectionLevel="administrative">
              <CardDetails />
            </Protected>
          }
        />
        <Route
          path="/manage/station/add"
          element={
            <Protected accessProtectionLevel="administrative">
              <StationForm />
            </Protected>
          }
        />
        <Route
          path="/manage/station/edit/:id"
          element={
            <Protected accessProtectionLevel="administrative">
              <StationForm />
            </Protected>
          }
        />
        <Route
          path="/manage/station/:id"
          element={
            <Protected accessProtectionLevel="administrative">
              <StationForm />
            </Protected>
          }
        />
        <Route
          path="/manage/stations"
          element={
            <Protected accessProtectionLevel="administrative">
              <Stations />
            </Protected>
          }
        />

        {/* Dashboards específicos por tipo de usuário */}
        <Route
          path="/dashboard/seller"
          element={
            <Protected accessProtectionLevel="operational">
              <SellerDashboard />
            </Protected>
          }
        />
        <Route
          path="/dashboard/admin"
          element={
            <Protected accessProtectionLevel="administrative">
              <AdminDashboard />
            </Protected>
          }
        />

        <Route
          path="/m/profile"
          element={
            <Protected accessProtectionLevel="authenticated">
              <Profile />
            </Protected>
          }
        />
      </Routes>
    </Suspense>
  );
}
